export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

interface LogEntry {
  timestamp: string
  level: string
  message: string
  context?: Record<string, any>
  error?: Error
  userId?: string
  sessionId?: string
}

class Logger {
  private minLevel: LogLevel = LogLevel.INFO
  private isDevelopment: boolean = import.meta.env.DEV

  constructor() {
    // Em desenvolvimento, mostrar todos os logs
    if (this.isDevelopment) {
      this.minLevel = LogLevel.DEBUG
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel
  }

  private formatMessage(level: LogLevel, message: string, context?: Record<string, any>, error?: Error): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel[level],
      message,
      context,
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } as any : undefined
    }

    // Adicionar contexto do usuário se disponível
    try {
      const userContext = this.getUserContext()
      if (userContext) {
        entry.userId = userContext.userId
        entry.sessionId = userContext.sessionId
      }
    } catch {
      // Ignorar erros ao obter contexto do usuário
    }

    return entry
  }

  private getUserContext(): { userId?: string; sessionId?: string } | null {
    try {
      // Tentar obter do sessionStorage ou localStorage
      const session = sessionStorage.getItem('user-session')
      if (session) {
        const parsed = JSON.parse(session)
        return {
          userId: parsed.userId,
          sessionId: parsed.sessionId
        }
      }
    } catch {
      // Ignorar erros de parsing
    }
    return null
  }

  private output(entry: LogEntry): void {
    if (!this.shouldLog(LogLevel[entry.level as keyof typeof LogLevel])) {
      return
    }

    const logMessage = `[${entry.timestamp}] ${entry.level}: ${entry.message}`
    
    if (this.isDevelopment) {
      // Em desenvolvimento, usar console com cores
      switch (entry.level) {
        case 'DEBUG':
          console.debug(logMessage, entry.context || '', entry.error || '')
          break
        case 'INFO':
          console.info(logMessage, entry.context || '', entry.error || '')
          break
        case 'WARN':
          console.warn(logMessage, entry.context || '', entry.error || '')
          break
        case 'ERROR':
          console.error(logMessage, entry.context || '', entry.error || '')
          break
      }
    } else {
      // Em produção, enviar para serviço de logging (futuro)
      this.sendToLoggingService(entry)
    }
  }

  private sendToLoggingService(entry: LogEntry): void {
    // TODO: Implementar envio para serviço de logging (Sentry, LogRocket, etc.)
    // Por enquanto, apenas console.error para erros críticos
    if (entry.level === 'ERROR') {
      console.error(entry.message, entry.context, entry.error)
    }
  }

  /**
   * Log de debug - apenas em desenvolvimento
   */
  debug(message: string, context?: Record<string, any>): void {
    const entry = this.formatMessage(LogLevel.DEBUG, message, context)
    this.output(entry)
  }

  /**
   * Log informativo
   */
  info(message: string, context?: Record<string, any>): void {
    const entry = this.formatMessage(LogLevel.INFO, message, context)
    this.output(entry)
  }

  /**
   * Log de aviso
   */
  warn(message: string, context?: Record<string, any>): void {
    const entry = this.formatMessage(LogLevel.WARN, message, context)
    this.output(entry)
  }

  /**
   * Log de erro
   */
  error(message: string, error?: Error, context?: Record<string, any>): void {
    const entry = this.formatMessage(LogLevel.ERROR, message, context, error)
    this.output(entry)
  }

  /**
   * Log de performance
   */
  performance(operation: string, duration: number, context?: Record<string, any>): void {
    this.info(`Performance: ${operation}`, {
      ...context,
      duration: `${duration}ms`,
      type: 'performance'
    })
  }

  /**
   * Log de evento de usuário
   */
  userEvent(event: string, context?: Record<string, any>): void {
    this.info(`User Event: ${event}`, {
      ...context,
      type: 'user-event'
    })
  }

  /**
   * Log de API call
   */
  apiCall(method: string, url: string, status: number, duration: number, context?: Record<string, any>): void {
    const level = status >= 400 ? LogLevel.ERROR : LogLevel.INFO
    const entry = this.formatMessage(level, `API Call: ${method} ${url}`, {
      ...context,
      method,
      url,
      status,
      duration: `${duration}ms`,
      type: 'api-call'
    })
    this.output(entry)
  }
}

// Instância singleton
export const logger = new Logger()

// Funções de conveniência para compatibilidade
export const log = {
  debug: (message: string, context?: Record<string, any>) => logger.debug(message, context),
  info: (message: string, context?: Record<string, any>) => logger.info(message, context),
  warn: (message: string, context?: Record<string, any>) => logger.warn(message, context),
  error: (message: string, error?: Error, context?: Record<string, any>) => logger.error(message, error, context),
  performance: (operation: string, duration: number, context?: Record<string, any>) => logger.performance(operation, duration, context),
  userEvent: (event: string, context?: Record<string, any>) => logger.userEvent(event, context),
  apiCall: (method: string, url: string, status: number, duration: number, context?: Record<string, any>) => logger.apiCall(method, url, status, duration, context)
}

// Export default para uso direto
export default logger